interface ActiveEvents {
    audit?: boolean;
    authentications?: boolean;
    dailyInsights?: boolean;
    integrations?: boolean;
    newIncident?: boolean;
    newInsight?: boolean;
    policyMatch?: boolean;
    riskLevel?: boolean;
    saAlerts?: boolean;
    settings?: boolean;
}

interface Integration {
    id: string;
    name: string;
    enabled: boolean;
    url: string;
    creationDate: number;
    lastModified: number;
    activeEvents: ActiveEvents;
    settings: unknown;
}

export interface WebhookIntegration extends Integration {
    url: string;
}

export interface SyslogServerIntegration extends Integration {
    port: string;
    protocol: string;
    splunkTimezone: string;
    target: string;
}

export interface EmailIntegration extends Integration {
    emails: string[];
}

export interface CustomIntegration {
    id: string;
    name: string;
    config: Record<string, unknown>;
    creationDate: number;
    lastModifiedDate: number;
    enabled: boolean;
    custom?: {
        testPassed?: boolean;
        testFailureMessage?: string;
        deactivationDisabled?: boolean;
        deactivationDisabledReason?: string;
    };
    activeEvents?: ActiveEvents;
    settings?: Record<string, unknown>;
}

export enum IntegrationType {
    NOTIFICATIONS = 'notifications',
    EDR = 'EDR',
    RISK_INGESTION = 'Risk Ingestion',
    ITSM = 'itsm'
}

interface MetadataFieldProperties {
    type: string;
    description?: string;
    format?: string;
}

export interface FieldProperties extends MetadataFieldProperties {
    displayName: string;
    enum?: string[];
    items?: {
        type: string;
    };
    minimum?: number;
    maximum?: number;
    defaultValue?: unknown;
    example: string;
    required?: string[];
    properties?: Record<string, FieldProperties>;
}

export interface DropdownFieldProperties extends FieldProperties {
    enum: string[];
}

interface ConfigSchema {
    type: string;
    description: string;
    required: string[];
    properties: Record<string, FieldProperties>;
}

interface CustomIntegrationSchema {
    activeEvents?: unknown;
    creationDate: MetadataFieldProperties;
    config: ConfigSchema;
    custom?: Record<string, unknown>;
    enabled: MetadataFieldProperties;
    id: MetadataFieldProperties;
    lastModifiedDate: MetadataFieldProperties;
    name: MetadataFieldProperties;
    settings: unknown;
}

interface ProviderSchema {
    type: string;
    minItems?: number;
    uniqueItems?: boolean;
    version?: string;
    deprecated?: boolean;
    description: string;
    documentationUrl: string;
    integrationType: IntegrationType;
    logo: string;
    name: string;
    isIntegrationHandledByTower?: boolean;
    testable: boolean;
    periodicallyTestable: boolean;
    supportedEvents: Record<
        string,
        {
            isCustomizable: boolean;
            defaultValue: boolean;
            defaultCustomValues: unknown;
            isEditable?: boolean;
        }
    >;
    items: {
        type: string;
        properties: CustomIntegrationSchema;
        required: string[];
    };
}

export interface CustomIntegrationsSchema {
    $schema?: string;
    type?: 'object';
    properties: Record<string, ProviderSchema>;
}
